/**
 * Map VendorResource rows (Module B) into grid ResourceOption rows.
 *
 * Attachments are first-class catalogue rows (`resourceType: ATTACHMENT`) with
 * their own ids — do not invent negative ids the way HiredEquipmentMaster did.
 * An attachment is related to a unit only by belonging to the same bundle, not
 * by a parent-resource link.
 *
 * Bundles are the hireable combo (unit + attachment + operator). They do not
 * carry their own unit/OT/vehicleType, and they often have no own rate — those
 * live on the components (HE-M04). The picker must resolve them from components
 * or a bundle line shows blank rate/unit and submit cannot snapshot it.
 */
import type { VendorResource } from '../../../services/vendorService';
import type { ResourceOption } from '../types';
import { liveApprovedRate } from './liveRate';
import { isAwaitingFirstRate } from '../../vendors/utils/rateDisplay';

export interface CatalogueResourceAttributes {
  rate: number | null;
  unit: string | null;
  vehicleType: string | null;
  overtimeEligible: boolean;
}

function trimOrNull(value: string | null | undefined): string | null {
  const trimmed = value?.trim();
  return trimmed ? trimmed : null;
}

/**
 * Own approved rate, or — for a bundle with no own rate — the sum of component
 * rates × qty (client mirror of `resolveBundlePrice`). Incomplete component
 * rates yield null rather than under-pricing the bundle.
 */
export function catalogueAttributes(
  resource: VendorResource,
  catalogue: VendorResource[],
  serviceAreaId?: number | null,
): CatalogueResourceAttributes {
  const ownRate = liveApprovedRate(resource.rates, serviceAreaId);
  const ownUnit = trimOrNull(resource.unit);
  const ownVehicleType = trimOrNull(resource.vehicleType);
  const ownOt = resource.overtimeEligible === true;

  if (resource.resourceType !== 'BUNDLE' || !resource.bundleComponents?.length) {
    return {
      rate: ownRate,
      unit: ownUnit,
      vehicleType: ownVehicleType,
      overtimeEligible: ownOt,
    };
  }

  const byId = new Map(catalogue.map((row) => [row.id, row]));
  const components = resource.bundleComponents.map((link) => ({
    qty: link.qty > 0 ? link.qty : 1,
    row: byId.get(link.componentResourceId),
  }));

  let rate = ownRate;
  if (rate == null) {
    let total = 0;
    let incomplete = components.length === 0;
    for (const { qty, row } of components) {
      const componentRate = liveApprovedRate(row?.rates, serviceAreaId);
      if (componentRate == null) {
        incomplete = true;
        break;
      }
      total += componentRate * qty;
    }
    rate = incomplete ? null : total;
  }

  const resolved = components.map((c) => c.row).filter((row): row is VendorResource => row != null);
  return {
    rate,
    unit: ownUnit ?? resolved.find((row) => trimOrNull(row.unit))?.unit ?? null,
    vehicleType: ownVehicleType ?? resolved.find((row) => trimOrNull(row.vehicleType))?.vehicleType ?? null,
    overtimeEligible: ownOt || resolved.some((row) => row.overtimeEligible === true),
  };
}

export function vendorResourceLabel(resource: VendorResource): string {
  if (resource.resourceType === 'BUNDLE' && resource.bundleComponents?.length) {
    const parts = resource.bundleComponents
      .map((link) => link.componentResource?.name?.trim())
      .filter((name): name is string => Boolean(name));
    if (parts.length > 0) {
      const joined = parts.join(' + ');
      const name = resource.name.trim();
      if (!name || name.toLowerCase() === joined.toLowerCase()) return joined;
      return `${name} (${joined})`;
    }
  }
  return resource.name;
}

export function vendorResourcesToResourceOptions(
  items: VendorResource[],
  serviceAreaId?: number | null,
): ResourceOption[] {
  return items
    // V2-007: a resource whose first rate is still awaiting approval is not offered.
    .filter((item) => item.isActive && !isAwaitingFirstRate(item))
    .filter((item) => {
      if (serviceAreaId == null || !item.serviceAreas?.length) return true;
      return item.serviceAreas.some((link) => link.serviceAreaId === serviceAreaId && link.isActive);
    })
    .map((item) => {
      const attrs = catalogueAttributes(item, items, serviceAreaId);
      return {
        id: item.id,
        label: vendorResourceLabel(item),
        rate: attrs.rate,
        unit: attrs.unit,
        vehicleType: attrs.vehicleType,
        overtimeEligible: attrs.overtimeEligible,
      };
    });
}

function resourceLabelKey(label: string | null | undefined): string {
  return (label ?? '').trim().toLowerCase();
}

/**
 * Resource picker for a PO-backed row: PO lines first, then the rest of that
 * subcontractor's catalogue. Dedupes extras that are already on the PO (same
 * label, or an id that would collide in the select).
 *
 * PO option `id`s are the catalogue VendorResource id when the line is linked
 * (`equipmentMasterId`); catalogue extras use the same id space.
 */
export function mergePoAndVendorResources(
  poResources: ResourceOption[],
  vendorResources: ResourceOption[],
): ResourceOption[] {
  if (poResources.length === 0) return vendorResources;
  if (vendorResources.length === 0) return poResources;

  const poIds = new Set(poResources.map((resource) => resource.id));
  const poCatalogueIds = new Set(
    poResources
      .map((resource) => resource.catalogueId)
      .filter((id): id is number => id != null),
  );
  const poLabels = new Set(
    poResources.map((resource) => resourceLabelKey(resource.label)).filter((label) => label.length > 0),
  );
  const extra = vendorResources.filter((resource) => {
    if (poIds.has(resource.id) || poCatalogueIds.has(resource.id)) return false;
    const label = resourceLabelKey(resource.label);
    return !(label && poLabels.has(label));
  });
  if (extra.length === 0) return poResources;
  return [
    ...poResources.map((resource) => ({ ...resource, onPurchaseOrder: true })),
    ...extra.map((resource) => ({ ...resource, onPurchaseOrder: false })),
  ];
}
